import { Checkbox } from "@/components/ui/checkbox";
import { Collapse } from "@/components/ui/collapse";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";

type ZoneSelectionProps = {
  cityNeighborhoods: { city: string; neighborhoods: string[] }[];
  selectedZones: string[];
  onCityToggle: (city: string) => void;
  onNeighborhoodToggle: (city: string, neighborhood: string) => void;
  getSelectedZones: () => string[];
};

export const ZoneSelection = ({
  cityNeighborhoods,
  selectedZones,
  onCityToggle,
  onNeighborhoodToggle,
  getSelectedZones,
}: ZoneSelectionProps) => {
  const isCityMacroSelected = (city: string) => selectedZones.includes(city);

  const isNeighborhoodSelected = (city: string, neighborhood: string) =>
    isCityMacroSelected(city) || selectedZones.includes(`${city}-${neighborhood}`);

  const getSelectedZonesSummary = () => {
    return getSelectedZones().map((zone) =>
      zone.includes("-") ? zone : `${zone} (Zona completa)`
    );
  };

  return (
    <div>
      <Label className="mb-1 text-sm font-medium">Cobertura geográfica</Label>
      <p className="mb-3 text-xs text-muted-foreground">
        Seleccioná una ciudad para cubrir todos sus barrios (incluso los que agregues después).
        O elegí barrios puntuales para limitar la cobertura.
      </p>
      <div className="max-h-[60dvh] overflow-y-auto rounded-md border border-border p-3">
        <div className="flex flex-col gap-2">
          {cityNeighborhoods.map((cityData) => {
            const neighborhoods = cityData.neighborhoods;
            const cityMacroSelected = isCityMacroSelected(cityData.city);
            const selectedNeighborhoodsInCity = neighborhoods.filter((neighborhood) =>
              selectedZones.includes(`${cityData.city}-${neighborhood}`)
            );
            const isCitySelected =
              cityMacroSelected ||
              (neighborhoods.length > 0 &&
                selectedNeighborhoodsInCity.length === neighborhoods.length);
            const isCityPartiallySelected =
              !cityMacroSelected &&
              selectedNeighborhoodsInCity.length > 0 &&
              selectedNeighborhoodsInCity.length < neighborhoods.length;

            return (
              <div key={cityData.city}>
                <label className="flex min-h-11 items-center gap-2 py-2 font-medium">
                  <Checkbox
                    checked={isCityPartiallySelected ? "indeterminate" : isCitySelected}
                    onCheckedChange={() => onCityToggle(cityData.city)}
                  />
                  {cityData.city}
                </label>

                <Collapse isOpen={isCitySelected || isCityPartiallySelected}>
                  <div className="ml-6 mt-1">
                    <div className="flex flex-col">
                      {neighborhoods.map((neighborhood) => (
                        <label
                          key={neighborhood}
                          className="flex min-h-11 items-center gap-2 py-2 text-sm"
                        >
                          <Checkbox
                            checked={isNeighborhoodSelected(cityData.city, neighborhood)}
                            onCheckedChange={() =>
                              onNeighborhoodToggle(cityData.city, neighborhood)
                            }
                            className="size-3.5"
                          />
                          {neighborhood}
                        </label>
                      ))}
                    </div>
                  </div>
                </Collapse>

                {cityData.city !== cityNeighborhoods[cityNeighborhoods.length - 1].city && (
                  <Separator className="my-2" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected zones summary */}
      {getSelectedZonesSummary().length > 0 && (
        <div className="mt-3 border-t border-border/50 pt-3">
          <p className="mb-1 text-xs text-muted-foreground">Zonas seleccionadas:</p>
          <div className="flex flex-wrap gap-2">
            {getSelectedZonesSummary().map((zone) => (
              <span
                key={zone}
                className="rounded-full bg-muted px-2 py-1 text-xs font-medium text-foreground"
              >
                {zone}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
